import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import loginIllustration from "@/assets/landing/login-illustration.svg";
import logoWhite from "@/assets/landing/logo-white.svg";

/**
 * The two-panel layout shared by the public auth pages (/auth, /auth/reset-password),
 * laid out in the active language's direction:
 * illustration panel on desktop, dark form panel with the logo + language
 * toggle, and a centred narrow column for the page's own content.
 */
export function AuthShell({ children }: { children: ReactNode }) {
  const { t, language, setLanguage, direction } = useLanguage();

  return (
    <div className="flex min-h-screen" dir={direction}>
      {/* Illustration panel (desktop only) */}
      <div data-testid="illustration-panel" className="hidden lg:flex w-[45%] bg-[#f5efeb] items-center justify-center relative">
        <Link
          to="/"
          className="absolute top-5 end-5 flex items-center gap-1.5 text-sm font-medium text-black/60 hover:text-black transition-colors"
        >
          <ArrowLeft className="w-4 h-4 rtl:rotate-180" />
          {t("رجوع", "Back")}
        </Link>
        {/* SVG illustration — no separate logo, the SVG already contains it */}
        <img src={loginIllustration} alt="" className="w-full max-w-lg px-6" />
      </div>

      {/* Form panel */}
      <div className="flex-1 bg-[#030712] flex flex-col min-h-screen">
        <div className="flex items-center justify-between p-6">
          <div className="lg:hidden" data-testid="mobile-logo">
            <Link to="/">
              <img src={logoWhite} alt={t("دلالي", "Dallali")} className="h-8 w-auto" />
            </Link>
          </div>
          <div className="hidden lg:block" />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setLanguage(language === "ar" ? "en" : "ar")}
            className="text-white/70 hover:text-white hover:bg-white/10"
            data-testid="lang-toggle"
          >
            {language === "ar" ? "English" : "العربية"}
          </Button>
        </div>

        <div className="flex-1 flex items-center justify-center px-6 pb-8">
          <div className="w-full max-w-sm space-y-8 mx-auto flex flex-col items-center !text-center">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}
